import React, { useEffect, useRef, useState } from "react";

/**
 * LiveQueue — a subtle real-time ticker for the "project queue".
 *
 * Starts at `initial` and gently drifts every 12–28 s so the studio feels
 * continuously active. 70 % of ticks move the position DOWN by 1 (a project
 * ahead of the visitor just wrapped up); the remainder move UP by 1 (a fresh
 * enquiry landed ahead of them). Position is always clamped to [1, 11].
 *
 * The last event is announced in a tiny caption so the change reads as
 * intentional rather than glitchy. After ~8 minutes of activity the ticker
 * settles down to keep things believable for long-lingering visitors.
 *
 * Props
 *  - initial:      seed position returned by /api/contact
 *  - onChange(pos, weeks): fires when the position (and therefore the
 *                          "estimated kick-off") changes
 */
export default function LiveQueue({ initial, onChange }) {
  const [pos, setPos] = useState(initial);
  const [event, setEvent] = useState(null); // "advanced" | "new" | null
  const [pulse, setPulse] = useState(0);
  const settledRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => setPos(initial), [initial]);

  useEffect(() => {
    const startedAt = Date.now();

    const scheduleNext = () => {
      const gap = 12000 + Math.random() * 16000; // 12–28 s
      timerRef.current = setTimeout(tick, gap);
    };

    const tick = () => {
      // After 8 min of ticking, stop to keep it believable.
      if (Date.now() - startedAt > 8 * 60 * 1000) {
        settledRef.current = true;
        return;
      }
      setPos((current) => {
        const advance = Math.random() < 0.7; // 70 % move forward
        let next = current + (advance ? -1 : 1);
        if (next < 1) next = 1;
        if (next > 11) next = 11;
        if (next === current) {
          scheduleNext();
          return current;
        }
        setEvent(advance ? "advanced" : "new");
        setPulse((p) => p + 1);
        const weeks = Math.max(2, Math.round(next * 1.4));
        onChange?.(next, weeks);
        scheduleNext();
        return next;
      });
    };

    scheduleNext();
    return () => clearTimeout(timerRef.current);
  }, [onChange]);

  return (
    <div className="border-l-2 border-primary pl-5 py-2 relative" data-testid="queue-position-card">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2 w-2" aria-hidden>
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-70" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
        </span>
        <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Your position · live
        </p>
      </div>
      <p
        key={pulse}
        data-testid="queue-position-value"
        className="font-display text-5xl md:text-6xl mt-1 tabular-nums queue-pulse"
      >
        #{pos}
      </p>
      <p
        className="text-xs text-muted-foreground mt-1 min-h-[1em] transition-opacity"
        data-testid="queue-position-caption"
      >
        {event === "advanced" && "A project ahead of you just wrapped up."}
        {event === "new" && "A new brief just landed ahead of you."}
        {!event && "in the project queue"}
      </p>
      <style>{`
        @keyframes queue-pulse-anim {
          0% { transform: translateY(-4px); opacity: 0; }
          40% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .queue-pulse { animation: queue-pulse-anim 500ms ease-out; }
      `}</style>
    </div>
  );
}
